import { TAppDispatch, TGetState } from 'store/types'
import slice from 'store/features/projectData/slice'
import { PROCESSING_MEDIA_ITEMS, PROJECT_MEDIA_TYPES } from 'consts'
import {
  IMediaItem,
  IUploadMediaItem,
  MediaStatus,
  TPartialMediaItem
} from 'types/media'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { captureException } from '@sentry/react'
import {
  getAiImagesByIds_API,
  getAIVideoProjects_API,
  uploadFile_API
} from 'api'
import { updateMediaItemWithAIImageData } from 'utils/aiImage'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { ErrorKeys } from 'consts/errorKeys'
import { checkIfAIVideoIsGenerating } from 'containers/SubSidebar/AIVideoContent/utils'
import { AIVideoSocketMessageStatus } from 'types/aiVideo'
import { calculateMediaRect, mapMediaInputAndOutput } from 'utils/media'
import { FIT } from 'consts/canvas'
import { isBlobUrl } from 'utils'
import { createDuplicatedItem } from 'store/features/projectData/utils'
import { IAddMediaItemsPayload } from 'store/types/projectData'
import { withAppThunks } from 'store/with-app-thunks'
import { handleApiError } from 'store/utils'
import {
  getItemFromLocalStorage,
  removeLoadingProgressFromLocalStorage
} from 'utils/localStorage'
import { generationProgressService } from 'services/generationProgress'

const { actions } = slice

export interface IMediaFile {
  id: string
  filePath: string
  width: number
  height: number
  duration?: number
  smallFilePath?: string
}

const uploadMediaFiles = async (
  medias: IUploadMediaItem[]
): Promise<Array<IMediaFile>> => {
  const formData = new FormData()

  medias.forEach(media => {
    formData.append('file', media.file)
  })

  const mediaUploadResponse = await uploadFile_API(formData)
  return mapMediaInputAndOutput(medias, mediaUploadResponse)
}

const uploadMediasToProject = (medias: IUploadMediaItem[]) =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: TGetState) => {
      const uploadedMedias = await uploadMediaFiles(medias)

      const state = getState()
      const { width: projectWidth, height: projectHeight } =
        projectDataSelectors.defaultDimensions(state)
      const mediaItems = projectDataSelectors.mediaItems(state)

      const uploadedMediasWithDimensions = uploadedMedias.map(uploadedMedia => {
        const currentMediaItem = mediaItems.find(
          item => item.id === uploadedMedia.id
        )

        const mediaItemUpdates: TPartialMediaItem = {
          meta: {
            path: uploadedMedia.filePath,
            status: MediaStatus.OK
          }
        }

        if (
          currentMediaItem &&
          (currentMediaItem.width === 0 || currentMediaItem.height === 0)
        ) {
          const mediaRect = calculateMediaRect({
            mediaDimensions: {
              width: uploadedMedia.width,
              height: uploadedMedia.height
            },
            mode: FIT,
            canvasDimensions: {
              width: projectWidth,
              height: projectHeight
            },
            rotation: 0
          })

          mediaItemUpdates.meta.originalWidth = uploadedMedia.width
          mediaItemUpdates.meta.originalHeight = uploadedMedia.height
          mediaItemUpdates.width = mediaRect.width
          mediaItemUpdates.height = mediaRect.height
          mediaItemUpdates.coordinates = {
            x: mediaRect.x,
            y: mediaRect.y
          }

          if (uploadedMedia.smallFilePath) {
            mediaItemUpdates.meta.previewPath = uploadedMedia.smallFilePath
          }
        }

        return {
          id: uploadedMedia.id,
          updates: mediaItemUpdates,
          status: MediaStatus.OK
        }
      })

      dispatch(actions.updateUploadedMediaItems(uploadedMediasWithDimensions))
      dispatch(
        appThunks.recentlyUsedMediaThunks.updateRecentlyUsedItemsOnUpload(
          uploadedMedias
        )
      )
    }
  )

const uploadMedias = (medias: IUploadMediaItem[]) =>
  withAppThunks(
    appThunks =>
      async (dispatch: TAppDispatch): Promise<IMediaFile[]> => {
        const uploadedMedias = await uploadMediaFiles(medias)

        dispatch(
          appThunks.recentlyUsedMediaThunks.updateRecentlyUsedItemsOnUpload(
            uploadedMedias
          )
        )

        return uploadedMedias
      }
  )

const duplicateVisualMediaItem =
  (id: string) => (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const mediaItems = projectDataSelectors.mediaItems(state)
    const mediaItem = mediaItems.find(item => item.id === id)

    if (!mediaItem || isBlobUrl(mediaItem.meta.path)) {
      return
    }

    const duplicatedItem = createDuplicatedItem<IMediaItem>(
      mediaItems,
      id,
      'zIndex',
      'startTime'
    )

    if (duplicatedItem) {
      dispatch(actions.addMediaItems({ value: [duplicatedItem] }))
    }

    return duplicatedItem?.id
  }

const removeVisualMediaItem = (id: string) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()

    const mediaItems = projectDataSelectors.mediaItems(state)
    const mediaItem = mediaItems.find(item => item.id === id)!
    const assetId = mediaItem.assetId
    const itemsCountWithTargetAssetId = mediaItems.filter(
      item => item.assetId === assetId
    ).length
    if (itemsCountWithTargetAssetId === 1) {
      if (mediaItem.type === PROJECT_MEDIA_TYPES.aiImage) {
        dispatch(appThunks.cacheThunks.removeFromAiImageCache([assetId]))
        dispatch(appThunks.aiImageThunks.resetGenerateForm())
      } else if (mediaItem.type === PROJECT_MEDIA_TYPES.aiVideo) {
        dispatch(appThunks.cacheThunks.removeFromAiVideoCache([assetId]))
        dispatch(appThunks.aiVideoThunks.clearGenerationParams())
      }
    }
    dispatch(actions.removeVisualMediaItem(id))
  })

const syncPendingAiImageData =
  () => async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const projectDimensions = projectDataSelectors.defaultDimensions(state)
    const pendingAiImgMediaItems =
      projectDataSelectors.pendingAiImgMediaItems(state)

    if (pendingAiImgMediaItems.length === 0) {
      return
    }

    const assetIds = pendingAiImgMediaItems
      .map(item => Number(item.assetId))
      .filter(id => !isNaN(id))

    if (assetIds.length === 0) {
      return
    }

    try {
      const response = await getAiImagesByIds_API({ ids: assetIds })
      const images = response.data

      images.forEach(image => {
        const { width, height } = image
        const firstImage = image.images[0]
        const { id, filePath } = firstImage
        const mediaItem = pendingAiImgMediaItems.find(
          item => item.assetId === id.toString()
        )

        if (mediaItem) {
          dispatch(
            actions.updateMediaItem(
              updateMediaItemWithAIImageData(
                mediaItem,
                filePath,
                width,
                height,
                {
                  width: projectDimensions.width,
                  height: projectDimensions.height
                }
              )
            )
          )
        }
      })
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    }
  }

const syncPendingAIVideoData =
  () => async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const pendingAIVideoMediaItems =
      projectDataSelectors.pendingAIVideoMediaItems(state)

    const defaultDimensions = projectDataSelectors.defaultDimensions(state)

    if (pendingAIVideoMediaItems.length === 0) {
      return
    }

    const assetIds = pendingAIVideoMediaItems
      .filter(item => !!Number(item.assetId))
      .map(item => Number(item.assetId))

    try {
      const response = await getAIVideoProjects_API({ projectIds: assetIds })
      const videos = response.data

      videos.forEach(video => {
        const { id, videoUrl, videoDurationSeconds, status } = video
        const timelineItem = pendingAIVideoMediaItems.find(
          item =>
            checkIfAIVideoIsGenerating(item) && item.assetId === String(id)
        )
        if (timelineItem && status === AIVideoSocketMessageStatus.COMPLETED) {
          // Video is completed, update the timeline item

          const { width, height } = video
          const hasDimensions = !!width && !!height

          const mediaRect = hasDimensions
            ? calculateMediaRect({
                mediaDimensions: { width, height },
                mode: FIT,
                canvasDimensions: defaultDimensions,
                rotation: 0
              })
            : null

          const mediaRectUpdates = mediaRect
            ? {
                width: mediaRect.width,
                height: mediaRect.height,
                coordinates: { x: mediaRect.x, y: mediaRect.y }
              }
            : {}

          const originalDimensions = hasDimensions
            ? { originalWidth: width, originalHeight: height }
            : {}

          dispatch(
            actions.updateMediaItem({
              id: timelineItem.id,
              updates: {
                duration: videoDurationSeconds,
                ...mediaRectUpdates,
                meta: {
                  ...timelineItem.meta,
                  ...originalDimensions,
                  path: videoUrl,
                  originalDuration: videoDurationSeconds,
                  status: MediaStatus.OK
                }
              }
            })
          )
          removeLoadingProgressFromLocalStorage(timelineItem.id)
        }
      })
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    }
  }

const updateMediaAfterGeneration = (
  payload: {
    id: string
    updates: Partial<IMediaItem>
  },
  config?: {
    isUndoable?: boolean
  }
) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch, getState: TGetState) => {
    const { id, updates } = payload
    const { isUndoable = true } = config || {}
    if (isUndoable) {
      dispatch(actions.updateMediaItem({ id, updates }))
    } else {
      dispatch(
        actions.updateMediaItemWithoutUndoSave({
          id,
          updates,
          status: MediaStatus.OK,
          isCheckAssetId: false
        })
      )
    }

    const state = getState()
    const mediaItem = state.projectData.present.mediaItems.find(
      item => item.id === id
    )

    if (mediaItem && mediaItem.meta.status !== MediaStatus.PENDING) {
      dispatch(appThunks.recentlyUsedMediaThunks.addNewItems([mediaItem]))
    }
  })

const addMediaItems = ({
  value,
  config = { applySelection: true, isUndoable: true }
}: IAddMediaItemsPayload<IMediaItem>) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch) => {
    dispatch(actions.addMediaItems({ value, config }))

    const nonPendingMediaItems = value.filter(
      item => item.meta.status !== MediaStatus.PENDING
    )

    dispatch(
      appThunks.recentlyUsedMediaThunks.addNewItems(nonPendingMediaItems)
    )
    if (!config?.applySelection || value.length === 0) {
      return
    }
    dispatch(
      appThunks.editorThunks.selectSingleTimelineItem({
        id: value[0].id,
        type: value[0].type
      })
    )
  })

const uploadFromMediaLibrary = (payload: IAddMediaItemsPayload<IMediaItem>) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch) => {
    dispatch(actions.uploadFromMediaLibrary(payload))
    dispatch(appThunks.recentlyUsedMediaThunks.addNewItems(payload.value))
    const firstItem = payload.value[0]
    if (!firstItem) {
      return
    }
    dispatch(
      appThunks.editorThunks.selectSingleTimelineItem({
        id: firstItem.id,
        type: firstItem.type
      })
    )
  })

// Recalculate all media sizes/positions to FIT the current canvas dimensions
const refitAllMediaToCanvas =
  () => (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const { width: canvasWidth, height: canvasHeight } =
      projectDataSelectors.defaultDimensions(state)
    const mediaItems = projectDataSelectors.mediaItems(state)

    if (!mediaItems || mediaItems.length === 0) return

    const updates = mediaItems.map(item => {
      const originalWidth = item.meta.originalWidth || item.width
      const originalHeight = item.meta.originalHeight || item.height

      const rect = calculateMediaRect({
        mediaDimensions: { width: originalWidth, height: originalHeight },
        mode: FIT,
        canvasDimensions: { width: canvasWidth, height: canvasHeight },
        rotation: item.rotation
      })

      return {
        id: item.id,
        updates: {
          width: rect.width,
          height: rect.height,
          coordinates: { x: rect.x, y: rect.y },
          meta: {}
        } as TPartialMediaItem,
        status: MediaStatus.OK
      }
    })

    if (updates.length > 0) {
      dispatch(actions.updateUploadedMediaItems(updates))
    }
  }

const handleFailedGenerationForMediaItem = (targetMediaItemId: string) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch, getState: TGetState) => {
    if (targetMediaItemId) {
      generationProgressService.stop({
        mediaItemId: targetMediaItemId,
        shouldRemoveFromStorage: true
      })
      const processingItems = projectDataSelectors.processingItems(getState())
      const processingItemsFromLocalStorage =
        getItemFromLocalStorage(PROCESSING_MEDIA_ITEMS) || []

      const allProcessingItems = [
        ...processingItems,
        ...processingItemsFromLocalStorage
      ]
      const existingProcessingItem = allProcessingItems.find(
        item => item.id === targetMediaItemId
      )
      if (existingProcessingItem) {
        // Restore processing item’s previous data back to media item,
        // except the startTime(in case scenes were reordered during the generation)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { startTime, ...dataToRestore } = existingProcessingItem
        dispatch(
          appThunks.projectDataThunks.updateMediaItem({
            id: targetMediaItemId,
            updates: dataToRestore
          })
        )
        dispatch(
          appThunks.editorThunks.reselectActiveTimelineMediaItem(
            existingProcessingItem
          )
        )
        dispatch(
          appThunks.projectDataThunks.removeFromProcessingItems(
            existingProcessingItem.id
          )
        )
      } else {
        dispatch(
          appThunks.projectDataThunks.removeVisualMediaItem(targetMediaItemId)
        )
        dispatch(appThunks.editorThunks.setAsideOverlayContent(null))
      }
    }
  })

export const projectDataMediaThunks = {
  uploadMedias,
  uploadMediasToProject,
  duplicateVisualMediaItem,
  updateMediaAfterGeneration,
  uploadFromMediaLibrary,
  addMediaItems,
  removeVisualMediaItem,
  syncPendingAiImageData,
  syncPendingAIVideoData,
  refitAllMediaToCanvas,
  handleFailedGenerationForMediaItem
}
